import {TextField} from '@mui/material'
import {LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import {TimePicker} from '@mui/x-date-pickers/TimePicker'
import {Moment} from 'moment-timezone'
import {useCallback, useEffect, useState, FC} from 'react'

import {useUikitTranslation} from '../../../common'

import {NumberSelect} from './NumberSelect'

type TimeStepperProps = {
  date: Moment
  timezone: string
  dateBound: [Moment, Moment]
  onChange?: (dateUtc: Moment) => void
  'data-test-id'?: string
}

export const TimeStepper: FC<TimeStepperProps> = ({
  date,
  timezone,
  dateBound,
  onChange,
  'data-test-id': dataTestId
}) => {
  const {t} = useUikitTranslation()
  const [selectedTime, _setSelectedTime] = useState(date)
  const [minTime, maxTime] = dateBound
  const [open, setOpen] = useState(false)

  useEffect(() => {
    _setSelectedTime(date)
  }, [date, timezone])

  const setSelectedTime = useCallback(
    (date: Moment) => {
      _setSelectedTime(date)
      if (onChange) {
        onChange(date)
      }
    },
    [onChange]
  )

  const getPreviousTime = () => {
    return selectedTime.clone().subtract(10, 'minutes')
  }

  const getNextTime = () => {
    return selectedTime.clone().add(10, 'minutes')
  }

  const isSelectedTimeValid = selectedTime.isValid() && selectedTime.isBetween(minTime, maxTime)

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <NumberSelect
        title={t('timeStepper.label')}
        plusButtonProps={{
          onClick: () => setSelectedTime(getNextTime()),
          disabled: getNextTime().isAfter(maxTime),
          'data-test-id': `${dataTestId}-plus`
        }}
        minusButtonProps={{
          onClick: () => setSelectedTime(getPreviousTime()),
          disabled: getPreviousTime().isBefore(minTime),
          'data-test-id': `${dataTestId}-minus`
        }}
        error={!isSelectedTimeValid}
      >
        <TimePicker
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={selectedTime}
          onChange={(newValue) => {
            if (newValue) {
              setSelectedTime(newValue)
            }
          }}
          minTime={minTime}
          maxTime={maxTime}
          inputFormat="HH:mm"
          renderInput={({inputRef, inputProps}) => (
            <TextField
              data-test-id={dataTestId}
              ref={inputRef}
              inputProps={inputProps}
              sx={{
                width: '80px',
                backgroundColor: 'transparent',
                margin: 0,

                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '& .MuiInputBase-input': {
                  padding: 0,
                  textAlign: 'center'
                }
              }}
            />
          )}
        />
      </NumberSelect>
    </LocalizationProvider>
  )
}
